import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import { Col, Row } from 'reactstrap';
import { DateInput, Select, TextInput } from '@octano/global-ui';
import { Control, Path } from 'react-hook-form';
import { useValidations } from '../../../../hooks/useValidations';
import { months, years } from '../utils';
import { OfferListFormValues, ProcessListFormValues } from '../types';

interface Texts {
  specificDate: string;
  specificMonthDay: string;
  nextMonth: string;
  nextMonthDay: string;
  form: {
    selectDay: string;
    selectMonth: string;
    selectDate: string;
    selectYear: string;
    validityRange: string;
    selectDayOptional: string;
  };
}

type AllowedFormValues = ProcessListFormValues | OfferListFormValues;

interface UseFirstInstallmentRuleProps<T extends AllowedFormValues> {
  key: string;
  control: Control<T>;
}

export default function useFirstInstallmentRule<T extends AllowedFormValues>({
  key,
  control,
}: UseFirstInstallmentRuleProps<T>) {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_ACADEMIC_OFFER);

  const { msgValidations } = useValidations();

  const texts = useMemo(() => {
    const prefix = 'processList.modalForm.firstExpirationRuleOptions';
    return {
      firstExpirationRuleSubTitle: t(`${prefix}.firstExpirationRuleSubTitle`),
      specificDate: t(`${prefix}.specificDate`),
      specificMonthDay: t(`${prefix}.firstDayOfSpecificMonth`),
      nextMonth: t(`${prefix}.nextMonth`),
      nextMonthDay: t(`${prefix}.nextMonthDay`),
      specificDateSubtitle: t(`${prefix}.specificDateSubtitle`),
      specificMonthDaySubtitle: t(`${prefix}.firstDayOfSpecificMonthSubtitle`),
      nextMonthSubtitle: t(`${prefix}.nextMonthSubtitle`),
      nextMonthDaySubtitle: t(`${prefix}.nextMonthDaySubtitle`),
      form: {
        selectDay: t(`${prefix}.form.selectDay`),
        selectDayOptional: t(`${prefix}.form.selectDayOptional`),
        selectMonth: t(`${prefix}.form.selectMonth`),
        selectYear: t(`${prefix}.form.selectYear`),
        selectDate: t(`${prefix}.form.selectDate`),
        validityRange: t(`${prefix}.form.validityRange`),
      },
    };
  }, [t]);

  const firstInstallmentRuleOptions = useMemo(() => {
    return [
      {
        label: texts.specificDate,
        value: 'specificDate',
      },
      {
        label: texts.specificMonthDay,
        value: 'specificMonthDay',
      },
      { label: texts.nextMonth, value: 'nextMonth' },
      { label: texts.nextMonthDay, value: 'nextMonthDay' },
    ];
  }, [texts]);

  const dayField = useCallback(
    (texts: Texts, optional: boolean = false) => (
      <Row>
        <Col xs={12}>
          <TextInput
            name={'selectDay' as Path<T>}
            control={control}
            label={
              optional ? texts.form.selectDayOptional : texts.form.selectDay
            }
            rules={{
              required: optional ? false : msgValidations.required,
              maxLength: {
                value: 2,
                message: 'Máximo 2 dígitos',
              },
              pattern: {
                value: /^[0-9]*$/,
                message: msgValidations.invalidIntNumeric,
              },
            }}
          />
        </Col>
      </Row>
    ),
    [control, msgValidations],
  );

  const rangeField = useCallback(
    (texts: Texts) => (
      <Row>
        <Col xs={12}>
          <TextInput
            name={'validityRange' as Path<T>}
            control={control}
            label={texts.form.validityRange}
            rules={{
              pattern: {
                value: /^[0-9]*$/,
                message: msgValidations.invalidIntNumeric,
              },
              maxLength: {
                value: 2,
                message: 'Máximo 2 dígitos',
              },
            }}
          />
        </Col>
      </Row>
    ),
    [control, msgValidations],
  );

  const monthField = useCallback(
    (texts: Texts) => (
      <Row>
        <Col xs={12}>
          <Select
            name={'selectMonth' as Path<T>}
            control={control}
            label={texts.form.selectMonth}
            options={months}
            rules={{ required: msgValidations.required }}
          />
        </Col>
      </Row>
    ),
    [control, msgValidations],
  );

  const dateField = useCallback(
    (texts: Texts) => (
      <Row>
        <Col xs={12}>
          <DateInput
            name={'selectDate' as Path<T>}
            control={control}
            label={texts.form.selectDate}
            rules={{ required: msgValidations.required }}
          />
        </Col>
      </Row>
    ),
    [control, msgValidations],
  );

  const yearField = useCallback(
    (texts: Texts) => (
      <Row>
        <Col xs={12}>
          <Select
            name={'selectYear' as Path<T>}
            control={control}
            label={texts.form.selectYear}
            options={years}
            rules={{ required: msgValidations.required }}
          />
        </Col>
      </Row>
    ),
    [control, msgValidations],
  );

  const formFields = useMemo(() => {
    switch (key) {
      case 'specificDate':
        return (
          <Row>
            <Col xs={12}>
              <p className="fs-14">{texts.specificDateSubtitle}</p>
            </Col>
            <Col xs={6}>{dateField(texts)}</Col>
            <Col xs={6}>{rangeField(texts)}</Col>
          </Row>
        );
      case 'specificMonthDay':
        return (
          <Row>
            <Col xs={12}>
              <p className="fs-14">{texts.specificMonthDaySubtitle}</p>
            </Col>
            <Col xs={6}>{yearField(texts)}</Col>
            <Col xs={6}>{monthField(texts)}</Col>
            <Col xs={12}>{rangeField(texts)}</Col>
          </Row>
        );
      case 'nextMonth':
        return (
          <Row>
            <Col xs={12}>
              <p className="fs-14">{texts.nextMonthSubtitle}</p>
            </Col>
            <Col xs={12}>{rangeField(texts)}</Col>
          </Row>
        );
      case 'nextMonthDay':
        return (
          <Row>
            <Col xs={12}>
              <p className="fs-14">{texts.nextMonthDaySubtitle}</p>
            </Col>
            <Col xs={6}>{dayField(texts)}</Col>
            <Col xs={6}>{rangeField(texts)}</Col>
          </Row>
        );
      default:
        return null;
    }
  }, [dayField, monthField, dateField, rangeField, yearField, key, texts]);

  return {
    options: firstInstallmentRuleOptions,
    formFields,
  };
}
